import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { 
  Users, 
  Clock, 
  TrendingUp, 
  BarChart3, 
  MessageSquare,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import type { AttentionReport } from '@/services/analytics/attentionReportService';
import { ArticleHeatmapOverlay } from './ArticleHeatmapOverlay';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

interface PrintableReportProps {
  report: AttentionReport;
}

/**
 * 1-page printable report for advertisers.
 * Optimized for A4 printing and PDF export.
 */
export function PrintableReport({ report }: PrintableReportProps) {
  const engagementData = [
    { name: 'Lesing', value: report.metrics.engagementDistribution.passive, color: COLORS[0] },
    { name: 'Kai Chat', value: report.metrics.engagementDistribution.kai, color: COLORS[1] },
    { name: 'Quiz', value: report.metrics.engagementDistribution.quiz, color: COLORS[2] },
    { name: 'Debatt', value: report.metrics.engagementDistribution.debate, color: COLORS[3] },
    { name: 'Poll', value: report.metrics.engagementDistribution.poll, color: COLORS[4] },
  ].filter(d => d.value > 0);

  return (
    <div className="print:block print:p-0 print:bg-white" style={{ maxWidth: '210mm' }}>
      {/* Header - Briefr branding */}
      <div className="text-center border-b-4 border-primary pb-4 mb-6">
        <h1 className="text-4xl font-bold" style={{ color: '#ea384c' }}>BRIEFR</h1>
        <p className="text-xl mt-1">ATTENTION REPORT</p>
        <h2 className="text-2xl font-semibold mt-4">{report.name}</h2>
        <p className="text-muted-foreground">
          Periode: {format(new Date(report.dateRangeStart), 'dd. MMMM yyyy', { locale: nb })} - {format(new Date(report.dateRangeEnd), 'dd. MMMM yyyy', { locale: nb })}
        </p>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-muted/30">
          <CardContent className="pt-4 text-center">
            <Users className="h-8 w-8 mx-auto text-primary mb-2" />
            <p className="text-4xl font-bold">{report.metrics.totalReaders.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Lesere totalt</p>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="pt-4 text-center">
            <Clock className="h-8 w-8 mx-auto text-primary mb-2" />
            <p className="text-4xl font-bold">{Math.round(report.metrics.averageAttentionSeconds)}</p>
            <p className="text-sm text-muted-foreground">Sek. gj.snitt oppmerksomhet</p>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="pt-4 text-center">
            <TrendingUp className="h-8 w-8 mx-auto text-primary mb-2" />
            <p className="text-4xl font-bold">{Math.round(report.metrics.averageTAV)}</p>
            <p className="text-sm text-muted-foreground">Total Attention Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Section */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5" />
            Sammenligning med industri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between mb-1">
                <span className="font-medium text-primary">Briefr</span>
                <span className="font-bold">{Math.round(report.metrics.averageAttentionSeconds)} sekunder</span>
              </div>
              <div className="h-4 bg-primary rounded-full" />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-muted-foreground">Facebook-annonse (gjennomsnitt)</span>
                <span>1.7 sekunder</span>
              </div>
              <div 
                className="h-4 bg-muted-foreground/30 rounded-full" 
                style={{ width: `${(1.7 / report.metrics.averageAttentionSeconds) * 100}%` }}
              />
            </div>
          </div>
          <div className="text-center mt-4 pt-4 border-t">
            <Badge className="text-lg px-6 py-2 bg-primary text-primary-foreground">
              {Math.round(report.metrics.comparisonMultiplier)}x mer oppmerksomhet
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Engagement Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Engasjementsfordeling</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={engagementData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {engagementData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [`${value.toFixed(1)}%`, '']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {engagementData.map((item, index) => (
                <div key={item.name} className="flex items-center gap-1">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-xs">{item.name} ({item.value.toFixed(0)}%)</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Heatmap */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Attention Heatmap
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ArticleHeatmapOverlay
              zones={report.heatmapData}
              articleTitle={report.name}
              intervieweeName=""
              className="h-[200px]"
            />
          </CardContent>
        </Card>
      </div>

      {/* Kai Insights */}
      {report.kaiInsights.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Kai Insights - Zero-Party Data
            </CardTitle>
            <CardDescription>
              Hva leserne spør om - verdifull innsikt i kunnskapshull
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {report.kaiInsights.slice(0, 4).map((insight, index) => (
                <div key={index} className="flex items-start gap-2 p-2 bg-muted/50 rounded">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2">"{insight.queryText}"</p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {insight.count}x
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Total Hours - Footer */}
      <Card className="bg-primary text-primary-foreground">
        <CardContent className="py-6">
          <div className="text-center">
            <p className="text-lg opacity-90">Total uforstyrret oppmerksomhet levert</p>
            <p className="text-5xl font-bold mt-2">
              {report.metrics.totalAttentionHours.toFixed(1)} timer
            </p>
            <p className="text-sm opacity-80 mt-2">
              {Math.round(report.metrics.totalReaders * report.metrics.averageAttentionSeconds / 60)} minutter total engasjert tid
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center mt-6 text-xs text-muted-foreground">
        <p>Generert av Briefr Attention Engine • {format(new Date(), 'dd.MM.yyyy HH:mm', { locale: nb })}</p>
        <p className="mt-1">briefr.no</p>
      </div>
    </div>
  );
}

export default PrintableReport;
